import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlus,
  faSpinner,
  faTrash,
  faFloppyDisk,
  faXmark,
  faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons'
import { API_BASE_URL } from '../../utils/apiBaseUrl'
import { apiFetch } from '../../utils/apiFetch'
import {
  licenseTypeLabel,
  mapMunicipalityLicenseRow,
} from '../../utils/controlCenterMunicipalityLicenses'
import type {
  ControlCenterLicenseType,
  ControlCenterMunicipality,
  ControlCenterMunicipalityLevel,
} from '../../types/controlCenter'
import {
  featureCentroid,
  fetchTigerwebBoundary,
  isTigerwebBoundaryLevel,
  searchTigerwebJurisdictions,
  type TigerwebSearchHit,
} from '../../services/tigerwebBoundaryService'
import { isInsideKioskVirtualKeyboard } from '../../utils/kioskVirtualKeyboard'

const LEVELS: ControlCenterMunicipalityLevel[] = ['city', 'county', 'state', 'federal']
const LICENSE_TYPES: ControlCenterLicenseType[] = [
  'sos_registration',
  'business',
  'contractor',
  'electrical',
  'other',
]

type FormState = {
  name: string
  level: ControlCenterMunicipalityLevel
  state: string
  lat: string
  lng: string
  censusGeoid: string
  licenseType: ControlCenterLicenseType
  licenseNumber: string
  expirationDate: string
  obtainedDate: string
  annualCost: string
  qualifier: string
  licenseUrl: string
  licenseImageFront: string
  licenseImageBack: string
}

function emptyForm(): FormState {
  return {
    name: '',
    level: 'city',
    state: '',
    lat: '',
    lng: '',
    censusGeoid: '',
    licenseType: 'contractor',
    licenseNumber: '',
    expirationDate: '',
    obtainedDate: '',
    annualCost: '',
    qualifier: '',
    licenseUrl: '',
    licenseImageFront: '',
    licenseImageBack: '',
  }
}

function formFromRow(row: ControlCenterMunicipality): FormState {
  return {
    name: row.name,
    level: row.level,
    state: row.state,
    lat: Number.isFinite(row.lat) ? String(row.lat) : '',
    lng: Number.isFinite(row.lng) ? String(row.lng) : '',
    censusGeoid: row.censusGeoid || '',
    licenseType: row.licenseType,
    licenseNumber: row.licenseNumber,
    expirationDate: row.expirationDate || '',
    obtainedDate: row.obtainedDate || '',
    annualCost: row.annualCost == null ? '' : String(row.annualCost),
    qualifier: row.qualifier || '',
    licenseUrl: row.licenseUrl || '',
    licenseImageFront: row.licenseImageFront || '',
    licenseImageBack: row.licenseImageBack || '',
  }
}

function titleCase(value: string): string {
  return value.replace(/\b\w/g, (c) => c.toUpperCase())
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('lts_jwt_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

function optionalString(value: string): string | null {
  const trimmed = value.trim()
  return trimmed ? trimmed : null
}

function optionalDate(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return null
  return trimmed
}

/** Census state FIPS → postal abbrev (for TIGERweb county/place results). */
const STATE_FIPS_TO_ABBR: Record<string, string> = {
  '01': 'AL',
  '02': 'AK',
  '04': 'AZ',
  '05': 'AR',
  '06': 'CA',
  '08': 'CO',
  '09': 'CT',
  '10': 'DE',
  '11': 'DC',
  '12': 'FL',
  '13': 'GA',
  '15': 'HI',
  '16': 'ID',
  '17': 'IL',
  '18': 'IN',
  '19': 'IA',
  '20': 'KS',
  '21': 'KY',
  '22': 'LA',
  '23': 'ME',
  '24': 'MD',
  '25': 'MA',
  '26': 'MI',
  '27': 'MN',
  '28': 'MS',
  '29': 'MO',
  '30': 'MT',
  '31': 'NE',
  '32': 'NV',
  '33': 'NH',
  '34': 'NJ',
  '35': 'NM',
  '36': 'NY',
  '37': 'NC',
  '38': 'ND',
  '39': 'OH',
  '40': 'OK',
  '41': 'OR',
  '42': 'PA',
  '44': 'RI',
  '45': 'SC',
  '46': 'SD',
  '47': 'TN',
  '48': 'TX',
  '49': 'UT',
  '50': 'VT',
  '51': 'VA',
  '53': 'WA',
  '54': 'WV',
  '55': 'WI',
  '56': 'WY',
}

const STATE_ABBR_TO_FIPS: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_FIPS_TO_ABBR).map(([fips, abbr]) => [abbr, fips]),
)

function hitStateAbbr(hit: TigerwebSearchHit): string | null {
  if (hit.stateAbbr) return hit.stateAbbr
  if (hit.stateFips && STATE_FIPS_TO_ABBR[hit.stateFips]) {
    return STATE_FIPS_TO_ABBR[hit.stateFips]
  }
  return null
}

function formatGeoidHit(hit: TigerwebSearchHit): string {
  const abbr = hitStateAbbr(hit)
  const where = abbr ? `, ${abbr}` : ''
  return `${hit.name}${where} · ${hit.geoid}`
}

type PayloadResult =
  | { ok: true; body: Record<string, unknown> }
  | { ok: false; message: string }

function buildPayload(form: FormState): PayloadResult {
  const name = form.name.trim()
  const state = form.state.trim().toUpperCase()
  const licenseNumber = form.licenseNumber.trim()
  const lat = Number(form.lat)
  const lng = Number(form.lng)

  if (!name) return { ok: false, message: 'Name is required.' }
  if (!/^[A-Z]{2}$/.test(state)) {
    return { ok: false, message: 'State must be a 2-letter code (e.g. UT).' }
  }
  if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
    return { ok: false, message: 'Latitude must be a number between -90 and 90.' }
  }
  if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
    return { ok: false, message: 'Longitude must be a number between -180 and 180.' }
  }
  if (!licenseNumber) return { ok: false, message: 'License number is required.' }

  if (form.expirationDate.trim() && !optionalDate(form.expirationDate)) {
    return { ok: false, message: 'Expiration date must be YYYY-MM-DD.' }
  }
  if (form.obtainedDate.trim() && !optionalDate(form.obtainedDate)) {
    return { ok: false, message: 'Obtained date must be YYYY-MM-DD.' }
  }

  let annualCost: number | null = null
  if (form.annualCost.trim()) {
    annualCost = Number(form.annualCost)
    if (!Number.isFinite(annualCost)) {
      return { ok: false, message: 'Annual cost must be a number.' }
    }
  }

  return {
    ok: true,
    body: {
      name,
      level: form.level,
      state,
      lat,
      lng,
      censusGeoid: optionalString(form.censusGeoid),
      licenseType: form.licenseType,
      licenseNumber,
      expirationDate: optionalDate(form.expirationDate),
      obtainedDate: optionalDate(form.obtainedDate),
      annualCost,
      qualifier: optionalString(form.qualifier),
      licenseUrl: optionalString(form.licenseUrl),
      licenseImageFront: optionalString(form.licenseImageFront),
      licenseImageBack: optionalString(form.licenseImageBack),
    },
  }
}

export function MunicipalityLicensesManager() {
  const [rows, setRows] = useState<ControlCenterMunicipality[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<FormState>(() => emptyForm())
  const [listSearch, setListSearch] = useState('')
  const [filterState, setFilterState] = useState<string>('All')
  const [filterType, setFilterType] = useState<ControlCenterLicenseType | 'All'>('All')
  const [filterLevel, setFilterLevel] = useState<ControlCenterMunicipalityLevel | 'All'>('All')
  const [geoidQuery, setGeoidQuery] = useState('')
  const [geoidHits, setGeoidHits] = useState<TigerwebSearchHit[]>([])
  const [geoidSearching, setGeoidSearching] = useState(false)
  const [geoidSearchError, setGeoidSearchError] = useState<string | null>(null)
  const [geoidOpen, setGeoidOpen] = useState(false)
  const [geoidResolving, setGeoidResolving] = useState(false)
  const geoidWrapRef = useRef<HTMLDivElement | null>(null)

  const loadRows = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await apiFetch(`${API_BASE_URL}/municipality-licenses`, {
        headers: authHeaders(),
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok || !payload?.success) {
        throw new Error(payload?.message || `Failed to load licenses (${res.status})`)
      }
      const data = Array.isArray(payload.data) ? payload.data : []
      setRows(data.map((row: Record<string, unknown>) => mapMunicipalityLicenseRow(row)))
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load municipality licenses')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadRows()
  }, [loadRows])

  useEffect(() => {
    const onDocMouseDown = (event: MouseEvent) => {
      if (isInsideKioskVirtualKeyboard(event.target)) return
      if (!geoidWrapRef.current?.contains(event.target as Node)) {
        setGeoidOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [])

  useEffect(() => {
    if (!isTigerwebBoundaryLevel(form.level)) {
      setGeoidHits([])
      setGeoidSearching(false)
      setGeoidSearchError(null)
      return
    }
    const level = form.level

    const q = geoidQuery.trim()
    if (q.length < 2) {
      setGeoidHits([])
      setGeoidSearching(false)
      setGeoidSearchError(null)
      return
    }

    const controller = new AbortController()
    const timer = window.setTimeout(async () => {
      setGeoidSearching(true)
      setGeoidSearchError(null)
      try {
        const stateFips = STATE_ABBR_TO_FIPS[form.state.trim().toUpperCase()] || null
        const hits = await searchTigerwebJurisdictions(level, q, {
          signal: controller.signal,
          stateFips,
          limit: 20,
        })
        if (!controller.signal.aborted) {
          setGeoidHits(hits)
        }
      } catch (e: unknown) {
        if (e instanceof DOMException && e.name === 'AbortError') return
        if (!controller.signal.aborted) {
          setGeoidHits([])
          setGeoidSearchError(e instanceof Error ? e.message : 'Census search failed')
        }
      } finally {
        if (!controller.signal.aborted) setGeoidSearching(false)
      }
    }, 300)

    return () => {
      controller.abort()
      window.clearTimeout(timer)
    }
  }, [geoidQuery, form.level, form.state])

  const resetGeoidSearch = () => {
    setGeoidQuery('')
    setGeoidHits([])
    setGeoidOpen(false)
    setGeoidSearchError(null)
  }

  const stateOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.state).filter(Boolean))
    return [...set].sort((a, b) => a.localeCompare(b))
  }, [rows])

  const filteredRows = useMemo(() => {
    const q = listSearch.trim().toLowerCase()
    return rows
      .filter((row) => {
        if (filterState !== 'All' && row.state !== filterState) return false
        if (filterType !== 'All' && row.licenseType !== filterType) return false
        if (filterLevel !== 'All' && row.level !== filterLevel) return false
        if (!q) return true
        const haystack = [
          row.name,
          row.state,
          row.level,
          row.licenseNumber,
          licenseTypeLabel(row.licenseType),
          row.qualifier,
          row.censusGeoid,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        return haystack.includes(q)
      })
      .sort((a, b) => {
        const byState = a.state.localeCompare(b.state)
        if (byState !== 0) return byState
        return a.name.localeCompare(b.name)
      })
  }, [rows, listSearch, filterState, filterType, filterLevel])

  const resetForm = () => {
    setEditingId(null)
    setForm(emptyForm())
    resetGeoidSearch()
    setSuccess(null)
    setError(null)
  }

  const startEdit = (row: ControlCenterMunicipality) => {
    setEditingId(row.id)
    setForm(formFromRow(row))
    resetGeoidSearch()
    setSuccess(null)
    setError(null)
  }

  const patchForm = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const selectGeoidHit = async (hit: TigerwebSearchHit) => {
    const abbr = hitStateAbbr(hit)
    setGeoidResolving(true)
    setGeoidSearchError(null)
    try {
      setForm((prev) => ({
        ...prev,
        censusGeoid: hit.geoid,
        name: prev.name.trim() ? prev.name : hit.basename || hit.name,
        state: prev.state.trim() ? prev.state : abbr || prev.state,
        level: hit.level,
      }))
      resetGeoidSearch()

      const needsCoords = !form.lat.trim() || !form.lng.trim()
      if (needsCoords) {
        try {
          const fc = await fetchTigerwebBoundary(hit.level, hit.geoid)
          const center = featureCentroid(fc.features[0])
          if (center) {
            setForm((prev) => ({
              ...prev,
              lat: prev.lat.trim() ? prev.lat : String(Number(center.lat.toFixed(6))),
              lng: prev.lng.trim() ? prev.lng : String(Number(center.lng.toFixed(6))),
            }))
          }
        } catch {
          // GEOID is set; coords remain optional for the user to fill.
        }
      }
    } finally {
      setGeoidResolving(false)
    }
  }

  const clearCensusGeoid = () => {
    patchForm('censusGeoid', '')
    resetGeoidSearch()
  }

  const handleSave = async () => {
    const built = buildPayload(form)
    if (!built.ok) {
      setError(built.message)
      setSuccess(null)
      return
    }

    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const isEdit = Boolean(editingId)
      const url = isEdit
        ? `${API_BASE_URL}/municipality-licenses/${editingId}`
        : `${API_BASE_URL}/municipality-licenses`
      const res = await apiFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(built.body),
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok || !payload?.success) {
        throw new Error(
          payload?.message ||
            payload?.error ||
            (res.status === 403
              ? 'Forbidden — controlCenterManager permission required to save.'
              : `Save failed (${res.status})`),
        )
      }
      await loadRows()
      const saved = mapMunicipalityLicenseRow(payload.data as Record<string, unknown>)
      setEditingId(saved.id)
      setForm(formFromRow(saved))
      setSuccess(isEdit ? 'License updated.' : 'License created.')
      // Ensure the saved row isn't hidden by list filters.
      setFilterState('All')
      setFilterType('All')
      setFilterLevel('All')
      setListSearch('')
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save license')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!editingId) return
    const target = rows.find((r) => r.id === editingId)
    const label = target ? `${target.name} (${target.state})` : 'this license'
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return

    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const res = await apiFetch(`${API_BASE_URL}/municipality-licenses/${editingId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok || !payload?.success) {
        throw new Error(
          payload?.message ||
            (res.status === 403
              ? 'Forbidden — controlCenterManager permission required to delete.'
              : `Delete failed (${res.status})`),
        )
      }
      setRows((prev) => prev.filter((r) => r.id !== editingId))
      resetForm()
      setSuccess('License deleted.')
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to delete license')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="mb-3 shrink-0">
        <h2 className="text-lg font-semibold text-base-content">Municipality licenses</h2>
        <p className="text-sm text-base-content/60">
          Create, edit, and remove jurisdiction licenses used by Control Center.
        </p>
      </div>

      {error ? (
        <div className="alert alert-error mb-3 shrink-0 py-2 text-sm">
          <span>{error}</span>
        </div>
      ) : null}
      {success ? (
        <div className="alert alert-success mb-3 shrink-0 py-2 text-sm">
          <span>{success}</span>
        </div>
      ) : null}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-12">
        <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-base-300 bg-base-100 lg:col-span-4 xl:col-span-3">
          <div className="shrink-0 space-y-2 border-b border-base-300 p-3">
            <button
              type="button"
              className="btn btn-primary btn-sm w-full"
              onClick={resetForm}
              disabled={saving}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-1.5" />
              New license
            </button>
            <input
              type="search"
              className="input input-bordered input-sm w-full"
              placeholder="Search licenses…"
              value={listSearch}
              onChange={(e) => setListSearch(e.target.value)}
              aria-label="Search municipality licenses"
            />
            <div className="flex flex-wrap gap-1">
              <button
                type="button"
                className={`btn btn-xs ${filterState === 'All' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setFilterState('All')}
              >
                All states
              </button>
              {stateOptions.map((state) => (
                <button
                  key={state}
                  type="button"
                  className={`btn btn-xs ${filterState === state ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setFilterState(state)}
                >
                  {state}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1">
              <button
                type="button"
                className={`btn btn-xs ${filterType === 'All' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setFilterType('All')}
              >
                All types
              </button>
              {LICENSE_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`btn btn-xs ${filterType === type ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setFilterType(type)}
                >
                  {licenseTypeLabel(type)}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1">
              <button
                type="button"
                className={`btn btn-xs ${filterLevel === 'All' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setFilterLevel('All')}
              >
                All levels
              </button>
              {LEVELS.map((level) => (
                <button
                  key={level}
                  type="button"
                  className={`btn btn-xs ${filterLevel === level ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setFilterLevel(level)}
                >
                  {titleCase(level)}
                </button>
              ))}
            </div>
            <div className="text-xs text-base-content/60">
              {loading
                ? 'Loading…'
                : `${filteredRows.length} of ${rows.length} shown`}
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain [scrollbar-gutter:stable]">
            {loading ? (
              <div className="flex items-center gap-2 p-4 text-sm text-base-content/70">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                Loading licenses…
              </div>
            ) : filteredRows.length === 0 ? (
              <div className="p-4 text-sm text-base-content/70">
                {rows.length === 0
                  ? 'No municipality licenses yet.'
                  : 'No licenses match your filters.'}
              </div>
            ) : (
              <ul className="menu menu-sm w-full p-1">
                {filteredRows.map((row) => {
                  const selected = editingId === row.id
                  return (
                    <li key={row.id}>
                      <button
                        type="button"
                        className={`items-start rounded-lg ${selected ? 'active' : ''}`}
                        onClick={() => startEdit(row)}
                      >
                        <div className="min-w-0 w-full text-left">
                          <div className="mb-1 flex flex-wrap gap-1">
                            <span className="badge badge-outline badge-xs">{row.state}</span>
                            <span className="badge badge-ghost badge-xs">
                              {licenseTypeLabel(row.licenseType)}
                            </span>
                            <span className="badge badge-ghost badge-xs">
                              {titleCase(row.level)}
                            </span>
                          </div>
                          <div className="truncate font-medium">{row.name}</div>
                          <div className="truncate text-[11px] text-base-content/55">
                            #{row.licenseNumber}
                            {row.expirationDate ? ` · exp ${row.expirationDate}` : ''}
                          </div>
                        </div>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-base-300 bg-base-100 lg:col-span-8 xl:col-span-9">
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-base-300 px-4 py-3">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold">
                {editingId ? 'Edit license' : 'New license'}
              </h3>
              <p className="text-[11px] text-base-content/55">
                {editingId
                  ? 'Update fields and save, or delete this record.'
                  : 'Fill required fields and save to create a new record.'}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-1.5">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={resetForm}
                disabled={saving}
              >
                <FontAwesomeIcon icon={faXmark} className="mr-1" />
                Clear
              </button>
              {editingId ? (
                <button
                  type="button"
                  className="btn btn-error btn-outline btn-sm"
                  onClick={() => void handleDelete()}
                  disabled={saving}
                >
                  <FontAwesomeIcon icon={faTrash} className="mr-1" />
                  Delete
                </button>
              ) : null}
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => void handleSave()}
                disabled={saving}
              >
                <FontAwesomeIcon
                  icon={saving ? faSpinner : faFloppyDisk}
                  className={`mr-1 ${saving ? 'animate-spin' : ''}`}
                />
                Save
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 [scrollbar-gutter:stable]">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="form-control w-full md:col-span-2">
                <div className="label py-0 pb-1">
                  <span className="label-text text-xs">Name *</span>
                </div>
                <input
                  className="input input-bordered input-sm w-full"
                  value={form.name}
                  onChange={(e) => patchForm('name', e.target.value)}
                  placeholder="Jurisdiction name"
                />
              </label>

              <label className="form-control w-full">
                <div className="label py-0 pb-1">
                  <span className="label-text text-xs">State *</span>
                </div>
                <input
                  className="input input-bordered input-sm w-full uppercase"
                  value={form.state}
                  maxLength={2}
                  onChange={(e) => patchForm('state', e.target.value.toUpperCase())}
                  placeholder="UT"
                />
              </label>

              <label className="form-control w-full">
                <div className="label py-0 pb-1">
                  <span className="label-text text-xs">Level *</span>
                </div>
                <select
                  className="select select-bordered select-sm w-full"
                  value={form.level}
                  onChange={(e) =>
                    patchForm('level', e.target.value as ControlCenterMunicipalityLevel)
                  }
                >
                  {LEVELS.map((level) => (
                    <option key={level} value={level}>
                      {titleCase(level)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="form-control w-full">
                <div className="label py-0 pb-1">
                  <span className="label-text text-xs">License type *</span>
                </div>
                <select
                  className="select select-bordered select-sm w-full"
                  value={form.licenseType}
                  onChange={(e) =>
                    patchForm('licenseType', e.target.value as ControlCenterLicenseType)
                  }
                >
                  {LICENSE_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {licenseTypeLabel(type)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="form-control w-full">
                <div className="label py-0 pb-1">
                  <span className="label-text text-xs">License number *</span>
                </div>
                <input
                  className="input input-bordered input-sm w-full"
                  value={form.licenseNumber}
                  onChange={(e) => patchForm('licenseNumber', e.target.value)}
                  placeholder="Number or reciprocity"
                />
              </label>

              <label className="form-control w-full">
                <div className="label py-0 pb-1">
                  <span className="label-text text-xs">Latitude *</span>
                </div>
                <input
                  type="number"
                  step="any"
                  className="input input-bordered input-sm w-full"
                  value={form.lat}
                  onChange={(e) => patchForm('lat', e.target.value)}
                  placeholder="40.7608"
                />
              </label>

              <label className="form-control w-full">
                <div className="label py-0 pb-1">
                  <span className="label-text text-xs">Longitude *</span>
                </div>
                <input
                  type="number"
                  step="any"
                  className="input input-bordered input-sm w-full"
                  value={form.lng}
                  onChange={(e) => patchForm('lng', e.target.value)}
                  placeholder="-111.8910"
                />
              </label>

              <div className="form-control w-full md:col-span-2" ref={geoidWrapRef}>
                <div className="label py-0 pb-1">
                  <span className="label-text text-xs">Census GEOID</span>
                </div>
                {form.level === 'federal' ? (
                  <p className="text-xs text-base-content/60">
                    Not used for federal licenses (no Census boundary).
                  </p>
                ) : (
                  <div className="relative">
                    {form.censusGeoid ? (
                      <div className="flex items-center gap-2">
                        <div className="input input-bordered input-sm flex min-w-0 flex-1 items-center gap-2 font-mono">
                          <span className="truncate">{form.censusGeoid}</span>
                          {geoidResolving ? (
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin opacity-60" />
                          ) : null}
                        </div>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={clearCensusGeoid}
                          title="Clear GEOID"
                          aria-label="Clear GEOID"
                        >
                          <FontAwesomeIcon icon={faXmark} />
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <FontAwesomeIcon
                          icon={faMagnifyingGlass}
                          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs opacity-50"
                        />
                        <input
                          className="input input-bordered input-sm w-full pl-9"
                          value={geoidQuery}
                          onChange={(e) => {
                            setGeoidQuery(e.target.value)
                            setGeoidOpen(true)
                          }}
                          onFocus={() => setGeoidOpen(true)}
                          placeholder={
                            form.state.trim()
                              ? `Search ${form.level} in ${form.state.trim().toUpperCase()}…`
                              : `Search Census ${form.level} by name…`
                          }
                          autoComplete="off"
                        />
                        {geoidSearching ? (
                          <FontAwesomeIcon
                            icon={faSpinner}
                            className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-xs opacity-60"
                          />
                        ) : null}
                      </div>
                    )}

                    {!form.censusGeoid && geoidOpen && geoidQuery.trim().length >= 2 ? (
                      <ul className="menu absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-box border border-base-300 bg-base-100 p-1 shadow-lg">
                        {geoidSearchError ? (
                          <li className="disabled">
                            <span className="text-error text-xs">{geoidSearchError}</span>
                          </li>
                        ) : geoidSearching && geoidHits.length === 0 ? (
                          <li className="disabled">
                            <span className="text-xs opacity-70">Searching Census…</span>
                          </li>
                        ) : geoidHits.length === 0 ? (
                          <li className="disabled">
                            <span className="text-xs opacity-70">No matches</span>
                          </li>
                        ) : (
                          geoidHits.map((hit) => (
                            <li key={`${hit.level}:${hit.geoid}`}>
                              <button
                                type="button"
                                className="text-left text-sm"
                                onClick={() => void selectGeoidHit(hit)}
                              >
                                <span>{formatGeoidHit(hit)}</span>
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    ) : null}

                    <p className="mt-1 text-[11px] text-base-content/55">
                      Search Census TIGERweb by name using Level
                      {form.state.trim() ? ` + State (${form.state.trim().toUpperCase()})` : ''}.
                      Selection fills GEOID
                      {!form.name.trim() || !form.lat.trim() || !form.lng.trim()
                        ? ' and any empty name/lat/lng'
                        : ''}
                      .
                    </p>
                  </div>
                )}
              </div>

              <label className="form-control w-full">
                <div className="label py-0 pb-1">
                  <span className="label-text text-xs">Expiration date</span>
                </div>
                <input
                  type="date"
                  className="input input-bordered input-sm w-full"
                  value={form.expirationDate}
                  onChange={(e) => patchForm('expirationDate', e.target.value)}
                />
              </label>

              <label className="form-control w-full">
                <div className="label py-0 pb-1">
                  <span className="label-text text-xs">Obtained date</span>
                </div>
                <input
                  type="date"
                  className="input input-bordered input-sm w-full"
                  value={form.obtainedDate}
                  onChange={(e) => patchForm('obtainedDate', e.target.value)}
                />
              </label>

              <label className="form-control w-full">
                <div className="label py-0 pb-1">
                  <span className="label-text text-xs">Annual cost</span>
                </div>
                <input
                  type="number"
                  step="any"
                  className="input input-bordered input-sm w-full"
                  value={form.annualCost}
                  onChange={(e) => patchForm('annualCost', e.target.value)}
                  placeholder="Optional"
                />
              </label>

              <label className="form-control w-full md:col-span-2">
                <div className="label py-0 pb-1">
                  <span className="label-text text-xs">Qualifier</span>
                </div>
                <input
                  className="input input-bordered input-sm w-full"
                  value={form.qualifier}
                  onChange={(e) => patchForm('qualifier', e.target.value)}
                  placeholder="e.g. Reciprocity with state-level license"
                />
              </label>

              <label className="form-control w-full md:col-span-2">
                <div className="label py-0 pb-1">
                  <span className="label-text text-xs">License URL</span>
                </div>
                <input
                  className="input input-bordered input-sm w-full"
                  value={form.licenseUrl}
                  onChange={(e) => patchForm('licenseUrl', e.target.value)}
                  placeholder="https://…"
                />
              </label>

              <label className="form-control w-full md:col-span-2">
                <div className="label py-0 pb-1">
                  <span className="label-text text-xs">License image front (URL)</span>
                </div>
                <input
                  className="input input-bordered input-sm w-full"
                  value={form.licenseImageFront}
                  onChange={(e) => patchForm('licenseImageFront', e.target.value)}
                  placeholder="Optional URL or data reference"
                />
              </label>

              <label className="form-control w-full md:col-span-2">
                <div className="label py-0 pb-1">
                  <span className="label-text text-xs">License image back (URL)</span>
                </div>
                <input
                  className="input input-bordered input-sm w-full"
                  value={form.licenseImageBack}
                  onChange={(e) => patchForm('licenseImageBack', e.target.value)}
                  placeholder="Optional URL or data reference"
                />
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MunicipalityLicensesManager
